



















































































import { Component, Prop, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";

/**
 * Utils
 */
import classes from "@/config/wow/classes";
import * as externalLinks from "@/config/external-links";
import * as imageLinks from "@/config/image-links";
import armorTypes from "@/config/wow/armor-types";
import itemClasses from "@/config/wow/item-classes";
import itemSlots from "@/config/wow/item-slots";

import { getElegantDateString, getTimeSince } from "@/utils/date-helpers";
import { sortByAsc, sortByDesc } from "@/utils/sort-helpers";


/**
 * Components
 */
import CharacterCell from "@/components/_global/CharacterCell.vue";


/**
 * Types
 */
import { GetTeamRaidWishesResponse } from "@/api/teams/get-team-raid-wishes.response";
import { GetRaidsResponse } from "@/api/raids/get-raids.response";
import { GetOneTeamResponse } from "@/api/teams/get-one-team.response";
import { GetRostersResponse } from "@/api/rosters/get-rosters.response";

/**
 * VueX Modules
 */
const teamModule = namespace("team");

interface WishOfItemElement {
	characterId: string;
	character: GetRostersResponse.Character;
	wish: GetTeamRaidWishesResponse.Wish;
}

interface WishByItem {
	[itemId: number]: WishOfItemElement[];
}

interface DisplayedDataItem {
	loot: GetRaidsResponse.Loot;
	wishes: WishOfItemElement[];
}

/**
 * Rosters wishes page.
 */
@Component({
	components: {
		CharacterCell
	}
})
export default class WishesByItemTable extends Vue {
	/// Injections
	classes = classes;
	externalLinks = externalLinks;
	imageLinks = imageLinks;
	armorTypes = armorTypes;
	itemSlots = itemSlots;
	itemClasses = itemClasses;
	getTimeSince = getTimeSince;
	getElegantDateString = getElegantDateString;

	/// Props
	@Prop()
	roster!: GetRostersResponse.Roster;

	@Prop()
	activeRaid!: GetRaidsResponse.Raid;

	@Prop()
	activeBossId!: string | null;

	@Prop()
	wishes!: GetTeamRaidWishesResponse | null;

	@Prop()
	sortBy!: string | null;

	@Prop()
	showEmptyRows!: boolean;

	/// Attributes
	isLoading = false;


	/// VueX Modules
	@teamModule.State("team")
	currentTeam!: GetOneTeamResponse;

	get headers() {
		return [
			{
				text: "Item",
				value: "loot",
				align: "left",
				sort: sortByAsc(x => x.name),
				sortable: false,
				divider: true
			},
			...this.wishValuesChoices
				.sort(sortByDesc(x => x.val))
				.filter(x => x.val != 0)
				.map(c => ({
					text: this.$t(`wishes_choices.${c.val}`),
					value: c.val,
					align: "left",
					width: "300px",
					sortable: false,
					divider: true

				})),
		];
	}

	get wishesByItem() {
		if (this.wishes == null) {
			return null;
		}

		const wishesByCharacter = this.wishes;

		const result: WishByItem = {};

		for (const characterId of Object.keys(wishesByCharacter)) {
			const wishesForCharacter = wishesByCharacter[characterId].wishList;
			for (const bossId of Object.keys(wishesForCharacter)) {
				const wishesForCharacterAndBoss = wishesForCharacter[bossId];
				for (const wish of wishesForCharacterAndBoss) {
					// const characters = ;
					// const sortedCharacters = characters.sort(sortByAsc(x => x.name));

					if (!result.hasOwnProperty(wish.loot.itemID)) {
						result[wish.loot.itemID] = [];
					}

					const character = this.roster.characters.find(x => x.id == characterId) ?? null;
					if (character != null) {
						result[wish.loot.itemID].push({
							characterId: characterId,
							character: character,
							wish: wish
						});
					}
				}
			}
		}

		return result;
	}


	get displayedData() {
		const wishesByItem = this.wishesByItem;
		if (wishesByItem == null) {
			return [];
		}

		return this.activeRaid.bosses
			.filter(x => this.activeBossId == null || x.id == this.activeBossId)
			.reduce((acc: DisplayedDataItem[], curr): DisplayedDataItem[] => [
					...acc,
					...curr.loots.map(x => ({
						loot: x,
						wishes: wishesByItem.hasOwnProperty(x.itemId)
							? wishesByItem[x.itemId].sort(sortByAsc((w: WishOfItemElement) => w.character.class))
							: []
					}))
				],
				[])
			.filter(x => this.showEmptyRows || x.wishes.length > 0)
			.sort(sortByAsc((x: DisplayedDataItem) => x.loot.slot))
			.sort(sortByAsc((x: DisplayedDataItem) => x.loot.class));
	}

	get wishValuesChoices() {
		return [
			{val: 0},
			{val: 10},
			{val: 15},
			{val: 20},
			{val: 30}
		];
	}

	getSlotName(wishValueChoice: number) {
		return `item.${wishValueChoice}`;
	}
}
